import { AnalyticsFlowEvent, AppSource } from "@/analytics/types";
import { useAnalytics } from "@/contexts/AnalyticsContext";
import { useFlow } from "@/contexts/FlowContext";
import { useMedia } from "@/contexts/MediaContext";
import { useCurrentMedia, useQrProcessingEffect, useMediaStore } from "@/store";
import Button from "@scripts/react/components/Button";
import { Chip } from "@scripts/react/components/Chip";
import { twClassNames } from "@scripts/react/helpers/twClassNames";
import React, { useCallback, useRef, useEffect } from "react";
import { ThumbnailContentWithProgress } from "./ThumbnailContentWithProgress";
import { AddFilesButton } from "./AddFilesButton";
import { CurrentMedia } from "@/store/slices/mediaSlice";
import { MediaItem, MediaItemStatus } from "@/types/media";
import { useMediaUtils } from "@/hooks/useMediaUtils";
import { FileCarouselLayout } from "./FileCarousel";
import { useHasReachedUploadLimit } from "@/hooks/useUploadLimit";

export const FileSwitcher = ({
    orderedMediaItems,
    hasMultipleFiles,
    thumbnails,
    deleteMediaItem,
    isTestingNewFiles,
    isReviewStep,
    // setIsModalOpen,
    onAddMediaItems,
    isDownloading,
    isShareMode = false,
    layout = FileCarouselLayout.DEFAULT,
    isImproveMode = false,
    isComparisonMode = false,
    approvedImageIds,
    containerId = "thumbnail-container",
    autoSelectDisabled = false
}: {
    orderedMediaItems: MediaItem[];
    hasMultipleFiles: boolean;
    thumbnails: Record<string, string>;
    deleteMediaItem: (id: string) => void;
    isTestingNewFiles: boolean;
    isReviewStep: boolean;
    // setIsModalOpen: (isOpen: boolean) => void;
    onAddMediaItems: (files: File[]) => Promise<void>;
    isDownloading: boolean;
    isShareMode?: boolean;
    isImproveMode?: boolean;
    isComparisonMode?: boolean;
    approvedImageIds?: Set<string>;
    containerId?: string;
    layout?: FileCarouselLayout;
    autoSelectDisabled?: boolean;
}) => {
    const isModelLayout = layout === FileCarouselLayout.MODEL;
    const { flowLocked } = useFlow();
    const { track } = useAnalytics();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { markFileAsVisited } = useMedia();
    const currentMedia = useCurrentMedia();
    const media = useMediaStore();
    const currentIndex = orderedMediaItems.findIndex((f) => f.id === currentMedia?.file.id);
    const { setCurrentMedia } = useMediaUtils();

    // Track previous media items to detect newly added files
    const prevMediaItemIdsRef = useRef<Set<string>>(new Set());
    // Track if we've initialized to skip auto-select on first mount
    const hasInitializedRef = useRef(false);

    // Auto-select newly uploaded files.
    // Disabled when the parent swaps orderedMediaItems (e.g. flattening a video into
    // annotated frames) to prevent the switcher from treating those items as "new uploads".
    useEffect(() => {
        if (autoSelectDisabled) return;

        const currentIds = new Set(orderedMediaItems.map((item) => item.id));
        const prevIds = prevMediaItemIdsRef.current;

        // On first mount, just initialize the ref without auto-selecting
        // This prevents selecting the last file when the component mounts (e.g., on hover)
        if (!hasInitializedRef.current) {
            hasInitializedRef.current = true;
            prevMediaItemIdsRef.current = currentIds;
            return;
        }

        // In improve mode, ImproveProvider owns the selection logic - don't auto-select here.
        // Still update the ref so we don't accumulate stale diffs.
        if (isImproveMode) {
            prevMediaItemIdsRef.current = currentIds;
            return;
        }

        // Find newly added items (in current but not in previous)
        const newItems = orderedMediaItems.filter((item) => !prevIds.has(item.id));

        // If there are new items, select the last one (most recently added)
        if (newItems.length > 0) {
            const lastNewItem = newItems[newItems.length - 1];
            const mediaItem = media[lastNewItem.id];

            if (mediaItem) {
                const updatedCurrentMedia: CurrentMedia = { file: mediaItem };

                if (mediaItem.isVideo) {
                    const frame0 = media[mediaItem.frame0Id!];
                    if (frame0) {
                        updatedCurrentMedia.frame = frame0;
                        updatedCurrentMedia.frameSegment = 0;
                    } else {
                        // Don't auto-select videos without a frame - let triggerUpload handle it
                        // This prevents setting currentMedia without a frame, which would then
                        // block triggerUpload from setting it properly (due to skipIfSet: true)
                        prevMediaItemIdsRef.current = currentIds;
                        return;
                    }
                } else {
                    updatedCurrentMedia.frame = mediaItem;
                    updatedCurrentMedia.frameSegment = 0;
                }

                setCurrentMedia(updatedCurrentMedia);
            }
        }

        // Update ref for next comparison
        prevMediaItemIdsRef.current = currentIds;
    }, [orderedMediaItems, media, setCurrentMedia, autoSelectDisabled, isImproveMode]);

    // Memoized QR processing callbacks
    const handleQrFileProcessed = useCallback(
        async (file: File) => {
            console.log("🎠 Carousel: Processing QR file for upload:", file.name);
            if (!isReviewStep) {
                await onAddMediaItems([file]);
            }
        },
        [isReviewStep, onAddMediaItems]
    );

    const handleQrAnalytics = useCallback(
        (event: string, data: any) => {
            track({
                event: AnalyticsFlowEvent.FILE_UPLOADER_CLICKED,
                data: { ...data, source: "qr_code" }
            });
        },
        [track]
    );

    // QR processing - uses the same shared logic as file input
    useQrProcessingEffect({
        enabled: !flowLocked && (!isReviewStep ? !isTestingNewFiles : false), // disabled in review step (TestWithMoreFilesActionCard handles that)
        onFileProcessed: handleQrFileProcessed,
        onAnalytics: handleQrAnalytics
    });

    const hasReachedUploadLimit = useHasReachedUploadLimit(orderedMediaItems);

    const handleAddFiles = useCallback(() => {
        if (flowLocked) return;

        fileInputRef.current?.click();
    }, [flowLocked]);

    const handleFilesSelect = useCallback(
        async (e: React.ChangeEvent<HTMLInputElement>) => {
            const files = Array.from(e.target.files || []);
            await onAddMediaItems(files);
            e.target.value = "";
        },
        [onAddMediaItems]
    );

    const handleSetCurrentMedia = useCallback(
        (targetFile: any) => {
            const mediaItem = media[targetFile.id];

            if (mediaItem) {
                const updatedCurrentMedia: CurrentMedia = { file: mediaItem };

                if (mediaItem.isVideo) {
                    const frame0 = media[mediaItem.frame0Id!];

                    if (frame0) {
                        updatedCurrentMedia.frame = frame0;
                        updatedCurrentMedia.frameSegment = 0;
                    } else {
                        updatedCurrentMedia.frameSegment = 0;
                    }
                } else {
                    updatedCurrentMedia.frame = mediaItem;
                    updatedCurrentMedia.frameSegment = 0;
                }

                setCurrentMedia(updatedCurrentMedia);
            } else {
                console.warn("[FileSwitcher] Media item not found for target file", targetFile);
            }
        },
        [media, setCurrentMedia]
    );

    const handlePrevFile = useCallback(() => {
        if (flowLocked) return;

        if (orderedMediaItems.length > 0) {
            const prevIndex = currentIndex <= 0 ? orderedMediaItems.length - 1 : currentIndex - 1;
            const targetFile = orderedMediaItems[prevIndex];

            // Track file navigation
            track({
                event: AnalyticsFlowEvent.FILE_NAVIGATION_CLICKED,
                data: {
                    appSource: AppSource.RAPID,
                    navigationMethod: "previous_button",
                    fromIndex: currentIndex,
                    toIndex: prevIndex,
                    totalFiles: orderedMediaItems.length,
                    fileType: targetFile.file.type.startsWith("video/") ? "video" : "image"
                }
            });

            handleSetCurrentMedia(targetFile);

            // Mark file as visited
            if (targetFile.id) {
                markFileAsVisited(targetFile.id);
            }
        }
    }, [
        orderedMediaItems,
        currentIndex,
        flowLocked,
        track,
        handleSetCurrentMedia,
        markFileAsVisited
    ]);

    const handleNextFile = useCallback(() => {
        if (flowLocked) return;

        if (orderedMediaItems.length > 0) {
            const nextIndex = currentIndex >= orderedMediaItems.length - 1 ? 0 : currentIndex + 1;
            const targetFile = orderedMediaItems[nextIndex];

            // Track file navigation
            track({
                event: AnalyticsFlowEvent.FILE_NAVIGATION_CLICKED,
                data: {
                    appSource: AppSource.RAPID,
                    navigationMethod: "next_button",
                    fromIndex: currentIndex,
                    toIndex: nextIndex,
                    totalFiles: orderedMediaItems.length,
                    fileType: targetFile.file.type.startsWith("video/") ? "video" : "image"
                }
            });

            handleSetCurrentMedia(targetFile);

            // Mark file as visited
            if (targetFile.id) {
                markFileAsVisited(targetFile.id);
            }
        }
    }, [
        orderedMediaItems,
        currentIndex,
        flowLocked,
        track,
        handleSetCurrentMedia,
        markFileAsVisited
    ]);

    const onHandleThumbnailClick = useCallback(
        (evt: React.MouseEvent, fileId: string) => {
            if (flowLocked) {
                return;
            }

            const targetFile = orderedMediaItems.find((f) => f.id === fileId);
            const targetIndex = orderedMediaItems.findIndex((f) => f.id === fileId);

            // Track file navigation
            track({
                event: AnalyticsFlowEvent.FILE_NAVIGATION_CLICKED,
                data: {
                    appSource: AppSource.RAPID,
                    navigationMethod: "thumbnail_click",
                    fromIndex: currentIndex,
                    toIndex: targetIndex,
                    totalFiles: orderedMediaItems.length,
                    fileType: targetFile?.file.type.startsWith("video/") ? "video" : "image"
                }
            });

            handleSetCurrentMedia(targetFile);

            // Mark file as visited
            if (targetFile?.id) {
                markFileAsVisited(targetFile.id);
            }

            evt.stopPropagation();
        },
        [
            flowLocked,
            orderedMediaItems,
            currentIndex,
            track,
            handleSetCurrentMedia,
            markFileAsVisited
        ]
    );

    const onHandleDeleteFile = useCallback(
        (evt: React.MouseEvent, fileId: string) => {
            if (flowLocked) {
                return;
            }
            deleteMediaItem(fileId);
            evt.stopPropagation(); // Prevent triggering thumbnail click
        },
        [deleteMediaItem, flowLocked]
    );

    // Common thumbnail rendering logic
    const renderThumbnail = useCallback(
        (mediaItem: MediaItem, index: number) => {
            const thumbnail = thumbnails[mediaItem.id];
            const isSelected = mediaItem.id === currentMedia?.file.id;
            const fileType = mediaItem.fileType;
            const isUploading =
                mediaItem.status !== MediaItemStatus.COMPLETED &&
                mediaItem.status !== MediaItemStatus.ERROR;

            // Only example data files have storageUploadDirOverride set
            const isExampleData = !!mediaItem?.storageUploadDirOverride;

            const displayTitle = flowLocked
                ? "Please approve or reject predictions to switch files."
                : !isSelected
                  ? `Switch to ${mediaItem.file.name || `${fileType} #${index + 1}`}`
                  : "";

            // In improve mode or comparison mode, never allow deletion
            const canDelete =
                hasMultipleFiles &&
                !flowLocked &&
                !isImproveMode &&
                !isComparisonMode &&
                (!isShareMode || !isExampleData);

            // Check if this image is approved (for improve mode)
            const isApproved = isImproveMode && approvedImageIds?.has(mediaItem.id);

            return (
                <ThumbnailContentWithProgress
                    key={mediaItem.id}
                    mediaItem={mediaItem}
                    thumbnail={thumbnail}
                    isUploading={isUploading}
                    fileType={fileType}
                    fileName={mediaItem.file.name || `${fileType} #${index + 1}`}
                    index={index}
                    title={displayTitle}
                    onClick={(evt) => onHandleThumbnailClick(evt, mediaItem.id)}
                    onDelete={
                        canDelete
                            ? (evt) => {
                                  onHandleDeleteFile(evt, mediaItem.id);
                              }
                            : undefined
                    }
                    isShareMode={isShareMode}
                    isSelected={isSelected}
                    flowLocked={flowLocked}
                    isSingleFile={!hasMultipleFiles}
                    isApproved={isApproved}
                />
            );
        },
        [
            thumbnails,
            currentMedia?.file.id,
            flowLocked,
            isShareMode,
            isImproveMode,
            isComparisonMode,
            approvedImageIds,
            onHandleThumbnailClick,
            onHandleDeleteFile,
            hasMultipleFiles
        ]
    );

    // Share mode: horizontal layout without title, chip, upload button, and arrows
    if (isShareMode) {
        return (
            <div
                className={twClassNames(
                    "pointer-events-auto flex w-fit max-w-full gap-3 overflow-x-auto rounded-2xl bg-black bg-opacity-50 p-3 backdrop-blur-sm",
                    flowLocked && "pointer-events-none cursor-not-allowed opacity-50"
                )}
            >
                {orderedMediaItems.map((uploadedFile, index) =>
                    renderThumbnail(uploadedFile, index)
                )}
            </div>
        );
    }

    // Default/Model mode: vertical layout with all features
    // In model layout: left border instead of rounded corners, sticks to file preview
    return (
        <div
            className={twClassNames(
                "fileCarouselWindow w-24 flex-shrink-0",
                isModelLayout && "!rounded-none !border-0 border-l border-gray-200 !shadow-none",
                flowLocked && "cursor-not-allowed opacity-50"
            )}
        >
            <div
                className={twClassNames(
                    "fileCarouselHeader h-12",
                    isModelLayout && "!rounded-none"
                )}
            >
                <h3 className="title">Files</h3>
                <Chip color="gray" type="number">
                    {orderedMediaItems.length}
                </Chip>
            </div>
            {/* Hide add files button in comparison mode */}
            {!isComparisonMode && (
                <div
                    className={twClassNames(
                        "border-border flex items-center justify-center border-b p-3",
                        isModelLayout && "border-none"
                    )}
                >
                    <AddFilesButton
                        onClick={handleAddFiles}
                        disabled={
                            isTestingNewFiles ||
                            flowLocked ||
                            isDownloading ||
                            (!isShareMode &&
                                !isImproveMode &&
                                !isReviewStep &&
                                hasReachedUploadLimit)
                        }
                        hasVideo={orderedMediaItems.some((item) => item.isVideo)}
                    />
                </div>
            )}

            {/* Thumbnail carousel */}
            <div
                id={containerId}
                className="flex min-h-0 flex-1 flex-col items-center justify-start gap-3 overflow-y-auto overflow-x-hidden p-3"
            >
                {orderedMediaItems.map((uploadedFile, index) =>
                    renderThumbnail(uploadedFile, index)
                )}
            </div>

            <div
                className={twClassNames(
                    "border-border grid grid-cols-2 border-t p-1",
                    isModelLayout && "border-none"
                )}
            >
                <Button
                    type="ghost"
                    onClick={handlePrevFile}
                    disabled={!hasMultipleFiles || flowLocked}
                    aria-label="Previous file"
                >
                    <i className="fa fa-chevron-up" />
                </Button>

                <Button
                    type="ghost"
                    onClick={handleNextFile}
                    disabled={!hasMultipleFiles || flowLocked}
                    aria-label="Next file"
                >
                    <i className="fa fa-chevron-down" />
                </Button>
            </div>

            {/* Hidden file input for adding files */}
            {/* TODO: Allow video uploads in improve mode by extracting frames and re-uploading as images */}
            <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={isImproveMode ? "image/*" : "image/*,video/*"}
                onChange={handleFilesSelect}
                disabled={flowLocked}
                className="hidden"
                aria-label="Select files to upload"
            />
        </div>
    );
};
